import * as Sentry from '@sentry/browser'
import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { ConfettiTarget } from '../../shared/Confetti'
import { MessageTopicKey } from '../../../api/gqlaxy/gql/generated/graphql'
import { selectCustomerPhotoUri } from '../../../store/selectors/customerProfile'
import { useSendListingMessage } from '../../../hooks/listings/useSendListingMessage'
import { Box, Button, ButtonGroup, Flex, Icon, Image } from '@homebotapp/hb-react-component-catalog'

const MSG = defineMessages({
  cancel: {
    id: 'ConfettiDirectMessage.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'ConfettiDirectMessage.confirm',
    defaultMessage: 'Confirm'
  },
  success: {
    id: 'ConfettiDirectMessage.success',
    defaultMessage: 'Direct message sent!'
  },
  fail: {
    id: 'ConfettiDirectMessage.fail',
    defaultMessage: 'Direct message failed to send.'
  }
})

/**
 * DM animation is based off EstimatedMonthlyPayment
 *
 * Goal is to eventually move this whole thing to the DS
 */
enum DMStatus {
  Fail = 'fail',
  Initial = 'initial',
  Success = 'success',
  Confirmation = 'confirmation'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer

  return function (...args) {
    clearTimeout(timer)

    timer = setTimeout(() => {
      // @ts-ignore
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

export interface ConfettiDirectMessageProps {
  topic: string
  message: string
  uiContext: string
  buttonText: string
}

export const ConfettiDirectMessage = ({ topic, message, uiContext, buttonText }: ConfettiDirectMessageProps) => {
  const intl = useIntl()
  const [isSending, setIsSending] = useState(false)
  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const [confettiCounter, setConfettiCounter] = useState(0)
  const topicKey = MessageTopicKey.BuyerGeneralInquiryToCustomer
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const { mutateAsync: sendBuyerMessage } = useSendListingMessage()
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  const handleSubmit = async () => {
    setIsSending(true)

    try {
      await sendBuyerMessage({
        input: {
          topic: topic,
          message: message,
          topicKey: topicKey
        }
      })

      setDmStatus(DMStatus.Success)
    } catch (error: any) {
      setDmStatus(DMStatus.Fail)

      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: error.message || '',
          requestData: error.config?.data || {},
          requestErrors: Object.values(error.response?.data?.errors) || []
        }
      })
    } finally {
      setTimeout(() => {
        setIsSending(false)
      }, 300)
    }
  }

  return (
    <Flex direction='column' align='center' maxW='25rem' w='100%'>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position='relative' mb={0} overflow='hidden' w='100%'>
              <Flex gap={4} direction='column' transition='.15s ease-in-out transform'>
                <Flex direction='column' gap={4}>
                  {dmStatus === DMStatus.Initial && (
                    <Button
                      py='8px'
                      width='100%'
                      bg='neutral.100'
                      variant='outline'
                      isLoading={isSending}
                      colorScheme='secondary'
                      onClick={() => setDmStatus(DMStatus.Confirmation)}
                      leftIcon={<Image src={customerPhoto} h={6} w={6} borderRadius='50%' />}
                      tracking={{
                        ui_context: uiContext,
                        guid: '9EbmBwuTbdaG53zZP2Laig',
                        descriptor: 'Initial Button Press to send message'
                      }}
                    >
                      {buttonText}
                    </Button>
                  )}
                  {dmStatus === DMStatus.Confirmation && (
                    <ButtonGroup width='100%'>
                      {!isSending && (
                        <Button
                          px={12}
                          bg='neutral.50'
                          variant='outline'
                          colorScheme='secondary'
                          w='auto'
                          transition='.2s ease-in-out all'
                          onClick={() => setDmStatus(DMStatus.Initial)}
                          tracking={{
                            ui_context: uiContext,
                            guid: 'rQgv5sT7aoHDqCgQHSA4tu',
                            descriptor: 'Confetti DM Cancel'
                          }}
                        >
                          {intl.formatMessage(MSG.cancel)}
                        </Button>
                      )}
                      <Button
                        w='100%'
                        isLoading={isSending}
                        colorScheme='primary'
                        onClick={() => handleSubmit()}
                        tracking={{
                          ui_context: uiContext,
                          guid: 'sKBuCcPN8QLJ67Dk6bXFgZ',
                          descriptor: 'Confetti DM Confirm'
                        }}
                      >
                        {intl.formatMessage(MSG.confirm)}
                      </Button>
                    </ButtonGroup>
                  )}
                </Flex>
                {(dmStatus === DMStatus.Success || dmStatus === DMStatus.Fail) && (
                  <Button
                    w='100%'
                    bg='neutral.50'
                    variant='outline'
                    colorScheme='secondary'
                    mt={-4}
                    onClick={() => debouncedHandleFinalStatus()}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    tracking={{
                      ui_context: uiContext,
                      guid: 'ncdqX8UKzrTLzsKnW1fW8X',
                      descriptor: 'Confetti DM Success / Fail'
                    }}
                  >
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                  </Button>
                )}
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
    </Flex>
  )
}
